import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

export const formatAmount = (amount: string | number | undefined | null) => {
  if (amount === undefined) return ''
  if (amount === null) return ''

  const regexp = new RegExp(/\B(?=(\d{3})+(?!\d))/, 'g')

  if (typeof amount === 'number') {
    return amount.toString().replace(regexp, ' ')
  }

  if (amount.length > 3) {
    const val = amount.replace(/ /g, '')
    return val.replace(regexp, ' ')
  }
  return amount
}

export const toNumericValue = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const numericValue = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(numericValue) ? numericValue : undefined
}

/**
 * Formats an amount rounded down to the nearest whole number with 'kr' currency, returning a fallback value if the amount is undefined or null.
 */
export const formatAmountWithCurrency = (amount?: string | number | null, fallback = '-') => {
  const numericAmount = toNumericValue(amount)
  if (numericAmount === undefined) {
    return fallback
  }

  return `${formatAmount(Math.floor(numericAmount))} kr`
}

export const formatPercentage = (value?: string | number | null, fallback = '-') => {
  const numericValue = toNumericValue(value)
  if (numericValue === undefined) {
    return fallback
  }
  return `${numericValue.toFixed(2).replace('.', ',')}%`
}

export const formatBooleanValue = (value?: boolean | null, fallback = '-') => {
  if (value === null || value === undefined) {
    return fallback
  }

  return value ? 'Ja' : 'Nej'
}

export const formatDateTimeValue = (date?: string | null, fallback = '-') => {
  if (!date) {
    return fallback
  }

  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) {
    return fallback
  }

  return parsed.toLocaleString('sv-SE')
}

export const formatDecimalAmount = (amount: string | number | undefined | null) => {
  if (amount === undefined) return ''
  if (amount === null) return ''

  const regexp = new RegExp(/\B(?=(\d{3})+(?!\d))/, 'g')

  let val = amount.toString().replace(/ /g, '')
  val = val.replace(/,/g, '.')

  const [beforeDot, ...afterDot] = val.split('.')
  const ints = beforeDot.replace(regexp, ' ')
  return afterDot.length ? `${ints}.${afterDot.join('')}` : ints
}

/**
 * Formats a date string if it is valid, otherwise returns the original value.
 * Does not accept undefined or null values
 */
export const formatDate = (val: string, format?: string): string => {
  if (val.toUpperCase().includes('T')) {
    return dayjs(val).isValid() ? dayjs(val).format(format ?? 'YYYY-MM-DD') : val
  }

  const date = dayjs(val, ['YY-MM-DD', 'YYMMDD', 'YYYY-MM-DD', 'YYYYMMDD'], 'sv', true)

  return date.isValid() ? date.format(format ?? 'YYYY-MM-DD') : val
}

/**
 * Formats a date string if it is valid, otherwise returns the original value.
 * Can return undefined or null
 */
export const formatDateIfValid = (val: string | undefined | null, format?: string): string | undefined | null => {
  if (!val || typeof val !== 'string') return val
  return formatDate(val, format)
}

export const parsePersonalNumber = (input: string) => {
  let pnr = input.replace(/\D/g, '')
  if (pnr.length === 10) {
    const pnrYear = pnr.substring(0, 2)
    // Assume that the applicant is at least 18 years old, if the year is higher than the current year minus 18, prepend "19", otherwise prepend "20"
    if (pnrYear <= (new Date().getFullYear() - 18).toString().substring(2)) {
      pnr = '20'.concat(pnr)
    } else {
      pnr = '19'.concat(pnr)
    }
  }
  return pnr
}

export const asNr = (val: unknown, defaultNr?: number) => {
  switch (typeof val) {
    case 'string': {
      const stringAsNumber = Number(val.replace(/\s/g, ''))
      if (typeof stringAsNumber !== 'number' || Number.isNaN(stringAsNumber)) {
        throw new Error(`Failed to convert string: ${val} to a number`)
      }
      return stringAsNumber
    }
    case 'number':
      return val
    default:
      if (defaultNr !== undefined) {
        return defaultNr
      }
      throw new Error(`Unexpected type: ${typeof val}`)
  }
}
export const formatLTV = (ltv: number, maxLTV?: number): string => {
  if (!maxLTV || ltv <= maxLTV) {
    return (ltv * 100).toFixed(2)
  }

  if (ltv > maxLTV + 0.0001) {
    return (ltv * 100).toFixed(2)
  }

  return (ltv * 100).toFixed(2)
}

// Format a string to title case, e.g. "hello world" -> "Hello World"
export const toTitleCase = (str: string) => str.toLowerCase().replace(/\b\p{L}/gu, (c) => c.toUpperCase())

/** "3 mån", "1 år", "2 år 3 mån" */
export const formatMonths = (months: number): string => {
  const years = Math.floor(months / 12)
  const remainingMonths = months % 12
  return (
    [years > 0 ? `${years} år` : '', remainingMonths > 0 ? `${remainingMonths} mån` : ''].filter(Boolean).join(' ') ||
    '0 mån'
  )
}

export const formatName = (user: { givenName?: string; surname?: string } | null | undefined) =>
  [user?.givenName, user?.surname].filter(Boolean).join(' ').trim() || undefined
